import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { colors, borderRadius, shadows } from '../styles/designTokens';
import { Customer } from '../models/CustomerModels';
import { getRelativeTimeKST } from '../utils/dateUtils';

// 테이블 컨테이너 스타일
const TableContainer = styled.div`
  background: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.lg};
  border: 1px solid ${colors.semantic.border.primary};
  box-shadow: ${shadows.base};
  overflow: hidden;
`;

// 테이블 스타일
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

// 테이블 헤더 스타일
const TableHeader = styled.thead`
  background: ${colors.semantic.background.secondary};
  border-bottom: 1px solid ${colors.semantic.border.primary};
`;

// 헤더 셀 스타일
const HeaderCell = styled.th`
  padding: 16px 12px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: ${colors.semantic.text.secondary};
  border-right: 1px solid ${colors.semantic.border.primary};
  
  &:last-child {
    border-right: none;
  }
`;

// 테이블 바디 스타일
const TableBody = styled.tbody``;

// 테이블 행 스타일
const TableRow = styled.tr`
  border-bottom: 1px solid ${colors.secondary[100]};

  &:hover {
    background: ${colors.semantic.background.secondary};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

// 데이터 셀 스타일
const DataCell = styled.td`
  padding: 16px 12px;
  font-size: 14px;
  color: ${colors.semantic.text.secondary};
  border-right: 1px solid ${colors.secondary[100]};
  
  &:last-child {
    border-right: none;
  }
`;

// 상태 배지 스타일 (추후 마케팅 동의 상태 표시용)
// const StatusBadge = styled.span<{ status: 'agree' | 'disagree' }>`
//   display: inline-block;
//   padding: 4px 8px;
//   border-radius: 4px;
//   font-size: 12px;
//   font-weight: 500;
//   
//   ${props => props.status === 'agree' ? `
//     background: success[100];
//     color: success[800];
//   ` : `
//     background: error[50];
//     color: error[800];
//   `}
// `;

// 금액 스타일
const AmountText = styled.span<{ amount: number }>`
  font-weight: 500;
  color: ${props =>
    props.amount > 0 ? colors.success[600] : colors.semantic.text.tertiary};
`;

// 상세보기 버튼 스타일
const DetailButton = styled.button`
  padding: 6px 12px;
  background: ${colors.primary[500]};
  color: ${colors.semantic.text.inverse};
  border: none;
  border-radius: ${borderRadius.base};
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: ${colors.primary[600]};
  }
`;

// 빈 상태 컨테이너 스타일
const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
`;

// 빈 상태 아이콘 스타일
const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

// 빈 상태 제목 스타일
const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.semantic.text.secondary};
  margin: 0 0 8px 0;
`;

// 빈 상태 설명 스타일
const EmptyDescription = styled.p`
  font-size: 14px;
  color: ${colors.semantic.text.tertiary};
  margin: 0;
  line-height: 1.5;
`;

// 로딩 메시지 스타일
const LoadingMessage = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${colors.semantic.text.tertiary};
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

// 로딩 스피너 스타일
const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${colors.neutral[200]};
  border-top: 2px solid ${colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// 고객 목록 테이블 Props 인터페이스
interface CustomerListTableProps {
  customers: Customer[];
  loading: boolean;
  formatDateTime: (dateTime: string) => string;
  formatCurrency: (amount: number) => string;
  formatMarketingStatus: (status: boolean) => string;
}

// 고객 목록 테이블 컴포넌트
const CustomerListTable: React.FC<CustomerListTableProps> = ({
  customers,
  loading,
  formatDateTime,
  formatCurrency,
  formatMarketingStatus
}) => {
  const navigate = useNavigate();

  // 디버깅 로그
  console.log('🔍 CustomerListTable 렌더링:', {
    customers,
    customersLength: customers?.length,
    loading,
    customersType: typeof customers,
    customersIsArray: Array.isArray(customers)
  });

  // 상세보기 핸들러 - 페이지 이동
  const handleDetailClick = (customer: Customer) => {
    console.log('고객 상세보기:', customer);
    navigate(`/customers/${customer.user_idx}`);
  };

  // 로딩 상태
  if (loading) {
    return (
      <TableContainer>
        <LoadingMessage>
          <LoadingSpinner />
          데이터를 불러오는 중...
        </LoadingMessage>
      </TableContainer>
    );
  }

  // 빈 데이터 상태
  if (!customers || customers.length === 0) {
    console.log('🚫 CustomerListTable: 빈 데이터 상태', {
      customers,
      customersLength: customers?.length,
      customersType: typeof customers,
      customersIsArray: Array.isArray(customers)
    });
    
    return (
      <TableContainer>
        <EmptyStateContainer>
          <EmptyIcon>👥</EmptyIcon>
          <EmptyTitle>고객 데이터가 없습니다</EmptyTitle>
          <EmptyDescription>
            등록된 고객이 없거나 검색 조건에 맞는 고객이 없습니다.<br />
            다른 검색어를 시도해보세요.
          </EmptyDescription>
        </EmptyStateContainer>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <tr>
            <HeaderCell>이름 또는 회사</HeaderCell>
            <HeaderCell>계정 ID</HeaderCell>
            <HeaderCell>이메일</HeaderCell>
            <HeaderCell>결제 금액</HeaderCell>
            <HeaderCell>전체 키워드</HeaderCell>
            <HeaderCell>활성 키워드</HeaderCell>
            <HeaderCell>마지막 로그인</HeaderCell>
            <HeaderCell>상세 정보</HeaderCell>
          </tr>
        </TableHeader>
        <TableBody>
          {customers.map((customer) => (
            <TableRow key={customer.user_idx}>
              <DataCell>{customer.user_name}</DataCell>
              <DataCell>{customer.user_id}</DataCell>
              <DataCell>{customer.user_email}</DataCell>
              <DataCell>
                <AmountText amount={customer.user_paid}>
                  {formatCurrency(customer.user_paid)}
                </AmountText>
              </DataCell>
              <DataCell>
                <AmountText amount={customer.total_keywords}>
                  {customer.total_keywords.toLocaleString('ko-KR')}
                </AmountText>
              </DataCell>
              <DataCell>
                <AmountText amount={customer.active_keywords}>
                  {customer.active_keywords.toLocaleString('ko-KR')}
                </AmountText>
              </DataCell>
              <DataCell>{getRelativeTimeKST(customer.last_datetime)}</DataCell>
              <DataCell>
                <DetailButton onClick={() => handleDetailClick(customer)}>
                  보기
                </DetailButton>
              </DataCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CustomerListTable; 